/**
 * Vintage map prints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wall_art`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mapPrintsPack: ToilePack = {
    slug: 'map-prints',
    category: 'wall-art',
    name: 'Vintage map prints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'coast', label: 'Coastline', subject: 'a rugged coastline with hatched shallows, depth soundings, a scattering of small islands and a compass rose'},
        {key: 'city', label: 'City plan', subject: 'an old city plan of fine narrow streets, a river bending through it, a formal park square and a bridge'},
        {key: 'mountain', label: 'Mountains', subject: 'a mountain region in hachured relief, with a river valley, a lake and a marked pass'},
    ],
    styles: [
        {key: 'engraved', label: 'Engraved', style: 'a fine copperplate engraving, thin uniform hatched line work with dense cross-hatching for depth', palette: 'sepia and warm brown ink on aged cream paper', framing: 'The chart fills the sheet inside an engraved double-rule border, a clean white margin outside it.'},
        {key: 'blueprint', label: 'Blueprint', style: 'a cyanotype blueprint chart, crisp thin line work of even weight with no shading', palette: 'white ink on a deep prussian blue ground', framing: 'The chart runs full-bleed to the edges, small white tick marks ruling the four borders.'},
        {key: 'inkonly', label: 'Black ink', style: 'bold pen-and-ink drafting, clean closed line work of uniform stroke with stippled shading for depth', palette: 'black ink only', framing: 'The map sits centered on a plain white paper sheet, the full page used with an even margin.'},
    ],
    directions: ['a river delta instead', 'as a matching pair', 'for a boat cabin'],
    seo: {
        title: 'Vintage map prints: coastline, city plan, mountains',
        metaDescription: 'Nine vintage map prints — a coastline, a city plan and mountains — in engraved, blueprint or black-ink style, sepia on cream, no legible place names.',
        h1: 'Vintage map prints: coastline, city plan and mountains',
        tagline: 'Nine maps, three cartography subjects, three finishes each — sepia on cream, a decorative cartouche, no legible place names.',
        intro: 'The old chart as wall art: a coastline with hatched shallows and a compass rose, a city plan in fine engraved streets, a mountain region with hachured relief. These nine cross those three subjects with a classic engraved-sepia finish, a technical blueprint style and a clean black-ink treatment — the study-wall map, no legible place names, a cartouche left decorative.',
        useCases: [
            'a study or home-office wall',
            'a boat cabin or coastal home',
            'a gift for a traveler or sailor',
            'a set of two for a hallway',
        ],
        faq: [
            {q: 'Does the map show real, legible place names?', a: 'No — place names are kept illegible or decorative by design, so the map reads as art rather than a navigational chart.'},
            {q: 'What size do these print at?', a: 'Each map is drawn at A3 proportions with an even margin, so it prints clean at A3 or scales to A4.'},
            {q: 'Can I ask for a different subject, like a river delta?', a: 'Yes — the direction field takes that request and redraws it in the same three finishes.'},
        ],
    },
}
